import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-form-layout-inline.scss';

import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import {
  FORM_LAYOUT_INLINE_ATTRIBUTES,
  GAP_TYPES,
  IdsFormLayoutGapType,
  IdsFormLayoutLabelPosition,
  IdsFormLayoutLabelAlignment,
  IdsFormLayoutLabelWrap,
  IdsFormLayoutFieldHeight,
  IdsFormLayoutSize,
  PropagatedKeys,
  syncSettingToChild,
  LABEL_POSITIONS,
  LABEL_ALIGNMENTS,
  LABEL_WRAPS,
  FIELD_HEIGHTS,
  SIZE_VALUES
} from './ids-form-layout-common';

import type { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';
import { isLabelStateValid } from '../../mixins/ids-label-state-mixin/ids-label-state-common';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

const INLINE_LABEL_ID = 'ids-form-layout-inline-label';

const CHILD_FORM_CONTROL_SELECTORS = 'ids-input, ids-textarea, ids-dropdown, ids-date-picker, ids-time-picker, ids-checkbox, ids-radio, ids-lookup, ids-color-picker, ids-search-field, ids-spinbox, ids-multiselect, ids-upload, ids-editor';

/**
 * IDS Form Layout Inline Component
 * Groups multiple related form controls inline under one shared visible label.
 * @type {IdsFormLayoutInline}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part inline - the inline form layout container element
 * @part label - the shared label element
 * @part content - the inline content container
 */
@customElement('ids-form-layout-inline')
@scss(styles)
export default class IdsFormLayoutInline extends Base {
  constructor() {
    super();
  }

  /** Tracks which attributes this wrapper has propagated onto each child */
  #propagatedKeys: PropagatedKeys = new WeakMap();

  /**
   * Whether the user has interacted with (focused out of) the group yet. Like
   * an individual field, the shared required message only appears after the
   * group has been "touched", not on initial render.
   */
  #touched = false;

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      ...FORM_LAYOUT_INLINE_ATTRIBUTES
    ];
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#attachSlotChangeListener();
    this.#attachLabelClickListener();
    this.#attachValidationListeners();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback?.(name, oldValue, newValue);

    if (oldValue === newValue) return;

    if (name === attributes.LABEL) {
      this.#refreshLabel();
      return;
    }

    if ([
      attributes.LABEL_POSITION,
      attributes.LABEL_WIDTH,
      attributes.LABEL_ALIGNMENT,
      attributes.LABEL_WRAP,
      attributes.LABEL_STATE,
      attributes.COMPACT,
      attributes.FIELD_HEIGHT,
      attributes.SIZE,
      attributes.DISABLED,
      attributes.READONLY,
      attributes.REQUIRED
    ].includes(name)) {
      this.#propagateSettingsToChildren();
    }
  }

  template(): string {
    const label = this.label ?? '';
    const required = this.required ? ' <span class="ids-form-layout-inline-required" aria-hidden="true">*</span>' : '';
    const labelMarkup = label
      ? `<ids-text part="label" id="${INLINE_LABEL_ID}" class="ids-form-layout-inline-label">${label}${required}</ids-text>`
      : '';

    return `
      <div class="ids-form-layout-inline" part="inline" role="group"${label ? ` aria-labelledby="${INLINE_LABEL_ID}"` : ''}>
        ${labelMarkup}
        <div class="ids-form-layout-inline-content" part="content">
          <slot></slot>
        </div>
        <div class="ids-form-layout-inline-validation" part="validation"></div>
      </div>
    `;
  }

  #attachSlotChangeListener(): void {
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      this.onEvent('slotchange', slot, () => {
        this.#propagateSettingsToChildren();
        this.#attachValidationListeners();
      });
    }
  }

  /**
   * Listen for focus-out / change within the group so the shared required
   * message can be shown when the user leaves the group empty and cleared once
   * a value is entered — mirroring how an individual field validates on blur.
   * `focusout` (unlike `blur`) bubbles, so a single listener on the wrapper
   * catches blur from any nested child control.
   */
  #attachValidationListeners(): void {
    this.offEvent('focusout.inlinevalidation', this);
    this.offEvent('change.inlinevalidation', this);
    this.onEvent('focusout.inlinevalidation', this, () => {
      this.#touched = true;
      this.#updateValidation();
    });
    this.onEvent('change.inlinevalidation', this, () => this.#updateValidation());
  }

  /**
   * The editable (not readonly/disabled) child form controls.
   * @returns {HTMLElement[]} editable child controls
   */
  #editableChildren(): HTMLElement[] {
    return Array.from(this.querySelectorAll(CHILD_FORM_CONTROL_SELECTORS))
      .filter((c) => !c.hasAttribute(attributes.READONLY) && !c.hasAttribute(attributes.DISABLED)) as HTMLElement[];
  }

  /**
   * Whether every editable child currently has a value.
   * @returns {boolean} true if no editable child is empty
   */
  #isFilled(): boolean {
    return this.#editableChildren().every((child) => {
      const val = (child as any).value;
      return !(val === null || val === undefined || (typeof val === 'string' && val.trim() === ''));
    });
  }

  /**
   * Show or hide the shared "Required" validation message based on whether the
   * group is required and any editable child is still empty.
   */
  #updateValidation(): void {
    const container = this.shadowRoot?.querySelector('.ids-form-layout-inline-validation');
    if (!container) return;

    const invalid = this.#touched && this.required && this.#editableChildren().length > 0 && !this.#isFilled();
    if (invalid) {
      if (!container.firstChild) {
        const message = (this as any).localeAPI?.translate?.('Required') || 'Required';
        container.innerHTML = `<div class="validation-message error" type="error"><ids-icon icon="error" class="ids-icon"></ids-icon><ids-text error="true" class="message-text">${message}</ids-text></div>`;
      }
    } else {
      container.innerHTML = '';
    }
  }

  #attachLabelClickListener(): void {
    const labelEl = this.shadowRoot?.querySelector('.ids-form-layout-inline-label');
    if (labelEl) {
      this.onEvent('click', labelEl, () => this.#focusFirstChild());
    }
  }

  /**
   * Public hook used by a parent form-layout / group to ask this wrapper to
   * re-apply inherited settings to its own children.
   */
  propagateSettings(): void {
    this.#propagateSettingsToChildren();
  }

  /**
   * Focus the first focusable child form control. Mirrors native
   * `<label for>` click-to-focus behavior for the shared inline label.
   */
  #focusFirstChild(): void {
    const first = this.querySelector(CHILD_FORM_CONTROL_SELECTORS) as HTMLElement | null;
    if (!first) return;
    const tagName = first.tagName.toLowerCase();
    customElements.whenDefined(tagName).then(() => {
      (first as any).focus?.();
    }).catch(() => {
      first.focus?.();
    });
  }

  #refreshLabel(): void {
    if (!this.shadowRoot) return;
    this.shadowRoot.innerHTML = '';
    this.shadowRoot.innerHTML = this.template();
    this.#attachSlotChangeListener();
    this.#attachLabelClickListener();
    this.#attachValidationListeners();
    this.#propagateSettingsToChildren();
    // Re-evaluate so toggling `required` off immediately clears any message,
    // and toggling it on re-shows it when the group is still empty.
    this.#updateValidation();
  }

  /**
   * Propagate label and field settings to all child form controls.
   * Child-level attributes always win.
   */
  #propagateSettingsToChildren(): void {
    const children = this.querySelectorAll(CHILD_FORM_CONTROL_SELECTORS);

    children.forEach((child) => {
      const tagName = child.tagName.toLowerCase();
      if (!tagName.startsWith('ids-')) return;
      customElements.whenDefined(tagName).then(() => {
        this.#propagateToChild(child as HTMLElement);
      }).catch(() => {
        // Element may not be registered, silently ignore
      });
    });
  }

  /**
   * The shared visible label belongs to the wrapper. Child labels stay for
   * screen readers but are visually hidden when this component owns the row.
   * When the wrapper uses a side label, collapse child labels so they don't
   * reserve any vertical/horizontal space inside the inline row.
   * @param {HTMLElement} child - The child element
   */
  #propagateToChild(child: HTMLElement): void {
    // Resolve effective label position — own attribute first, then nearest parent form-layout.
    const parentLayout = this.closest('ids-form-layout') as any;
    const effectiveLabelPosition: IdsFormLayoutLabelPosition = this.labelPosition
      || (parentLayout?.labelPosition as IdsFormLayoutLabelPosition)
      || null;
    const isSideLabel = effectiveLabelPosition === 'inline-start' || effectiveLabelPosition === 'inline-end';

    // The wrapper owns the shared visible label, so child labels are removed
    // entirely (collapsed = no text and no reserved label space) for both side
    // and top label positions. An explicit wrapper `labelState` still wins.
    const desiredLabelState: IdsLabelStateMode = this.labelState || 'collapsed';

    if ('labelState' in child) {
      // Always force the desired state so children don't keep a previously
      // inherited 'hidden' state when the parent label-position changes.
      (child as any).labelState = desiredLabelState;
    }

    // For side-label wrappers, force child labels to render at the top so
    // their label slot (now collapsed) doesn't fight the inline row layout.
    if (isSideLabel && 'labelPosition' in child) {
      (child as any).labelPosition = 'top';
    }

    // Inline-row children are positioned by the wrapper's grid/flex layout, so
    // their default stacking bottom margin would offset them vertically from the
    // shared label. Drop it so the field box centers against the label.
    if ('noMargins' in child && !child.hasAttribute(attributes.NO_MARGINS)) {
      (child as any).noMargins = true;
    }

    const tracker = this.#propagatedKeys;
    syncSettingToChild(child, attributes.COMPACT, 'compact', this.compact, tracker, true);
    syncSettingToChild(child, attributes.FIELD_HEIGHT, 'fieldHeight', this.fieldHeight, tracker);
    syncSettingToChild(child, attributes.SIZE, 'size', this.size, tracker);
    syncSettingToChild(child, attributes.DISABLED, 'disabled', this.disabled, tracker, true);
    syncSettingToChild(child, attributes.READONLY, 'readonly', this.readonly, tracker, true);
  }

  #isValidGap(value: string | null): boolean {
    return GAP_TYPES.includes(value as IdsFormLayoutGapType);
  }

  // ================================================
  // Label Settings
  // ================================================

  set label(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
    }
  }

  get label(): string | null {
    return this.getAttribute(attributes.LABEL);
  }

  set labelPosition(value: IdsFormLayoutLabelPosition) {
    if (value && LABEL_POSITIONS.includes(value)) {
      this.setAttribute(attributes.LABEL_POSITION, value);
    } else {
      this.removeAttribute(attributes.LABEL_POSITION);
    }
  }

  get labelPosition(): IdsFormLayoutLabelPosition {
    return this.getAttribute(attributes.LABEL_POSITION) as IdsFormLayoutLabelPosition;
  }

  set labelWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_WIDTH, value);
      this.style.setProperty('--ids-form-layout-label-width', value);
    } else {
      this.removeAttribute(attributes.LABEL_WIDTH);
      this.style.removeProperty('--ids-form-layout-label-width');
    }
  }

  get labelWidth(): string | null {
    return this.getAttribute(attributes.LABEL_WIDTH);
  }

  set labelAlignment(value: IdsFormLayoutLabelAlignment) {
    if (value && LABEL_ALIGNMENTS.includes(value)) {
      this.setAttribute(attributes.LABEL_ALIGNMENT, value);
    } else {
      this.removeAttribute(attributes.LABEL_ALIGNMENT);
    }
  }

  get labelAlignment(): IdsFormLayoutLabelAlignment {
    return this.getAttribute(attributes.LABEL_ALIGNMENT) as IdsFormLayoutLabelAlignment;
  }

  set labelWrap(value: IdsFormLayoutLabelWrap) {
    if (value && LABEL_WRAPS.includes(value)) {
      this.setAttribute(attributes.LABEL_WRAP, value);
    } else {
      this.removeAttribute(attributes.LABEL_WRAP);
    }
  }

  get labelWrap(): IdsFormLayoutLabelWrap {
    return this.getAttribute(attributes.LABEL_WRAP) as IdsFormLayoutLabelWrap;
  }

  set labelState(value: IdsLabelStateMode) {
    if (isLabelStateValid(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_STATE, value);
      } else {
        this.removeAttribute(attributes.LABEL_STATE);
      }
    } else {
      this.removeAttribute(attributes.LABEL_STATE);
    }
  }

  get labelState(): IdsLabelStateMode {
    return this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode;
  }

  // ================================================
  // Layout Settings
  // ================================================

  set gap(value: IdsFormLayoutGapType) {
    if (this.#isValidGap(value as string)) {
      this.setAttribute(attributes.GAP, String(value));
    } else {
      this.removeAttribute(attributes.GAP);
    }
  }

  get gap(): IdsFormLayoutGapType {
    return this.getAttribute(attributes.GAP) as IdsFormLayoutGapType;
  }

  set columns(value: string | number | null) {
    if (value !== null && value !== undefined && value !== '') {
      this.setAttribute(attributes.COLUMNS, String(value));
    } else {
      this.removeAttribute(attributes.COLUMNS);
    }
  }

  get columns(): string | null {
    return this.getAttribute(attributes.COLUMNS);
  }

  set wrap(value: boolean | string) {
    const enabled = stringToBool(value);
    if (enabled) {
      this.setAttribute(attributes.WRAP, '');
    } else {
      this.removeAttribute(attributes.WRAP);
    }
  }

  get wrap(): boolean {
    if (!this.hasAttribute(attributes.WRAP)) return true;
    return stringToBool(this.getAttribute(attributes.WRAP));
  }

  // ================================================
  // State Settings (propagates to children)
  // ================================================

  set required(value: boolean | string) {
    const required = stringToBool(value);
    if (required) {
      this.setAttribute(attributes.REQUIRED, '');
    } else {
      this.removeAttribute(attributes.REQUIRED);
    }
    this.#refreshLabel();
  }

  get required(): boolean {
    return stringToBool(this.getAttribute(attributes.REQUIRED));
  }

  set disabled(value: boolean | string) {
    const disabled = stringToBool(value);
    if (disabled) {
      this.setAttribute(attributes.DISABLED, '');
    } else {
      this.removeAttribute(attributes.DISABLED);
    }
  }

  get disabled(): boolean {
    return stringToBool(this.getAttribute(attributes.DISABLED));
  }

  set readonly(value: boolean | string) {
    const readonly = stringToBool(value);
    if (readonly) {
      this.setAttribute(attributes.READONLY, '');
    } else {
      this.removeAttribute(attributes.READONLY);
    }
  }

  get readonly(): boolean {
    return stringToBool(this.getAttribute(attributes.READONLY));
  }

  set compact(value: boolean | string) {
    const compact = stringToBool(value);
    if (compact) {
      this.setAttribute(attributes.COMPACT, '');
    } else {
      this.removeAttribute(attributes.COMPACT);
    }
  }

  get compact(): boolean {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  set fieldHeight(value: IdsFormLayoutFieldHeight) {
    if (value && FIELD_HEIGHTS.includes(value)) {
      this.setAttribute(attributes.FIELD_HEIGHT, value);
    } else {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  get fieldHeight(): IdsFormLayoutFieldHeight {
    return this.getAttribute(attributes.FIELD_HEIGHT) as IdsFormLayoutFieldHeight;
  }

  set size(value: IdsFormLayoutSize) {
    if (value && SIZE_VALUES.includes(value)) {
      this.setAttribute(attributes.SIZE, value);
    } else {
      this.removeAttribute(attributes.SIZE);
    }
  }

  get size(): IdsFormLayoutSize {
    return this.getAttribute(attributes.SIZE) as IdsFormLayoutSize;
  }

  set enableContainer(value: boolean | string) {
    if (stringToBool(value)) {
      this.setAttribute(attributes.ENABLE_CONTAINER, '');
    } else {
      this.removeAttribute(attributes.ENABLE_CONTAINER);
    }
  }

  get enableContainer(): boolean {
    return stringToBool(this.getAttribute(attributes.ENABLE_CONTAINER));
  }
}
